import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import { getNewsById } from '../../services/newsService';
import './NewsPreviewPage.css';

const NewsPreviewPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [newsArticle, setNewsArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if we got news data from navigation state (from the input form)
  const previewData = location.state?.newsData;

  useEffect(() => {
    if (previewData) {
      // Use the preview data passed from the input form
      setNewsArticle(previewData);
      setLoading(false);
    } else if (id) {
      // Fetch from API if we have an ID
      fetchNewsArticle();
    } else {
      setError('No news article to preview');
      setLoading(false);
    }
  }, [id, previewData]);

  const fetchNewsArticle = async () => {
    try {
      setLoading(true);
      const article = await getNewsById(id);
      setNewsArticle(article);
    } catch (err) {
      setError('Failed to load news article');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="news-preview-loading">Loading preview...</div>
      </AdminLayout>
    );
  }

  if (error || !newsArticle) {
    return (
      <AdminLayout>
        <div className="news-preview-error">
          <h2>Preview Not Available</h2>
          <p>{error || 'No news article data available for preview.'}</p>
          <button onClick={() => navigate('/admin/news')} className="np-back-btn">
            Back to News Listing
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="news-preview-content">
        {/* Header */}
        <div className="np-header">
          <div className="np-header-left">
            <h1 className="np-page-title">News Preview</h1>
            <p className="np-page-description">Preview how your news article will appear to readers</p>
          </div>
          <div className="np-header-actions">
            <button className="np-back-btn" onClick={() => navigate(-1)}>
              ← Back
            </button>
            <button className="np-edit-btn" onClick={() => navigate(`/admin/news/edit/${newsArticle.id || ''}`)}>
              Edit Article
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="np-preview-container">
          <div className="np-article-preview">
            {/* Category & Date */}
            <div className="np-meta-info">
              {newsArticle.category && (
                <span className="np-category">{newsArticle.category}</span>
              )}
              <span className="np-date">{formatDate(newsArticle.publishedAt || newsArticle.createdAt)}</span>
            </div>

            {/* Title */}
            <h1 className="np-article-title">{newsArticle.title}</h1>

            {/* Author */}
            {newsArticle.authorName && (
              <div className="np-author">
                <span>By {newsArticle.authorName}</span>
              </div>
            )}

            {/* Main Image */}
            {newsArticle.image && (
              <div className="np-main-image">
                <img src={newsArticle.image} alt={newsArticle.title} />
              </div>
            )}

            {/* Excerpt */}
            {newsArticle.excerpt && (
              <div className="np-excerpt">
                <p>{newsArticle.excerpt}</p>
              </div>
            )}

            {/* Content */}
            <div className="np-article-content">
              {newsArticle.content?.split('\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>

            {/* Gallery Images */}
            {newsArticle.galleryImages && newsArticle.galleryImages.length > 0 && (
              <div className="np-gallery">
                <h3>Gallery</h3>
                <div className="np-gallery-grid">
                  {newsArticle.galleryImages.map((img, index) => (
                    <div key={index} className="np-gallery-item">
                      <img src={img} alt={`Gallery ${index + 1}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {newsArticle.tags && newsArticle.tags.length > 0 && (
              <div className="np-tags">
                <h4>Tags:</h4>
                <div className="np-tags-list">
                  {(typeof newsArticle.tags === 'string' ? JSON.parse(newsArticle.tags) : newsArticle.tags).map((tag, index) => (
                    <span key={index} className="np-tag">{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Views */}
            <div className="np-stats">
              <span className="np-views">{newsArticle.views || 0} views</span>
              <span className="np-status">Status: {newsArticle.status?.charAt(0).toUpperCase() + newsArticle.status?.slice(1) || 'Draft'}</span>
            </div>
          </div>

          {/* Related Articles Placeholder */}
          <div className="np-sidebar">
            <h3>How It Will Appear</h3>
            <p className="np-sidebar-info">
              This is a preview of your news article. The final appearance may vary 
              slightly based on the user's device and browser.
            </p>
            <div className="np-preview-card">
              {newsArticle.image && (
                <div className="np-card-image">
                  <img src={newsArticle.image} alt={newsArticle.title} />
                </div>
              )}
              <div className="np-card-content">
                <span className="np-card-category">{newsArticle.category || 'Uncategorized'}</span>
                <h4 className="np-card-title">{newsArticle.title}</h4>
                <p className="np-card-excerpt">
                  {newsArticle.excerpt || newsArticle.content?.substring(0, 100) + '...'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default NewsPreviewPage;
